/**
 * ScheduleTourModal — seller picks a date, time, and duration for a tour
 * (Schedule / Set Time / Reschedule). Uses ui/Modal for the shell.
 */
import React, { useEffect, useId, useState } from 'react';
import Modal from '../../ui/Modal';
import { TourModalDetails, TourModalFooter } from './TourModalParts';
import styles from './TourModals.module.css';

const DURATION_OPTIONS = [15, 30, 45, 60, 90];

/**
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is shown.
 * @param {string} [props.title] - Dialog title.
 * @param {Array<{label: string, value: string}>} [props.details] - Summary rows.
 * @param {number} [props.initialDuration] - Seeded from the buyer's requested
 *   length so confirming without touching the selector preserves it.
 * @param {boolean} [props.submitting] - Request in flight; locks the dialog.
 * @param {string|null} [props.error] - Error from the last attempt.
 * @param {(date: string, time: string, duration: number) => void} props.onConfirm
 * @param {() => void} props.onClose
 */
export default function ScheduleTourModal({
  open,
  title = 'Schedule tour',
  details = [],
  initialDuration = 30,
  submitting = false,
  error = null,
  onConfirm,
  onClose,
}) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(initialDuration);
  const dateId = useId();
  const timeId = useId();
  const durationId = useId();
  const promptId = useId();

  useEffect(() => {
    if (!open) return;
    setDate('');
    setTime('');
    setDuration(initialDuration);
  }, [open, initialDuration]);

  const handleClose = () => {
    if (!submitting) onClose?.();
  };

  const footer = (
    <TourModalFooter
      error={error}
      submitting={submitting}
      confirmLabel="Confirm time"
      busyLabel="Scheduling…"
      onDismiss={handleClose}
      onConfirm={() => onConfirm?.(date, time, duration)}
    />
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="sm"
      describedBy={promptId}
      footer={footer}
    >
      <p id={promptId} className={styles.prompt}>
        Pick the time for this tour. The buyer will be notified.
      </p>

      <TourModalDetails details={details} />

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label htmlFor={dateId} className={styles.label}>Date</label>
          <input
            id={dateId}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor={timeId} className={styles.label}>Time</label>
          <input
            id={timeId}
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>
      <div className={styles.field}>
        <label htmlFor={durationId} className={styles.label}>Duration</label>
        <select
          id={durationId}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          disabled={submitting}
        >
          {DURATION_OPTIONS.map((m) => (
            <option key={m} value={m}>{m} min</option>
          ))}
        </select>
      </div>
    </Modal>
  );
}
